/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 6th 2022, 2:19:34 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { Trash2, Settings as SettingsIcon } from "react-feather";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import ReactFlow, { NodeProps, EdgeProps, OnInit, OnMoveEnd, NodeDragHandler, OnConnect } from "react-flow-renderer";
import { MiniMap, Controls, Handle, Position, ReactFlowProvider } from "react-flow-renderer";
import { getBezierPath, getEdgeCenter } from "react-flow-renderer";
// import { useReactFlow, Background, BackgroundVariant } from "react-flow-renderer";
import { isEqual } from "lodash";
import { TaskTemplate, NextActivation, BoundaryEventConfiguration } from "lib/schemas";
import { getNewActivationDefault } from "lib/factories";
import { BOUNDARY_EVENT_NODE_SIZE } from "service-builder/adapters/constants";
import {
  useReactFlowContext,
  IReactFlowNodeData,
  getBoundaryEventNodeId,
} from "service-builder/adapters/ReactFlowAdapter";
import { useSplitNodeHandlers, useBoundaryEventHandlers } from "service-builder/hooks";
import { useWorkflowContext } from "service-builder/providers";
import { useWorkflowStore } from "service-builder/stores";
import { COLORSCHEME } from "theme/styles";
import { useToggleState, ActionDialog, EmphasisText } from "ui-widgets";
import { ActivationBranchForm } from "./ActivationBranchForm";
import { BoundaryEventConfigurationModal } from "./EventOchestrationSettings";
import {
  TaskTemplateListItemDefaultWidget,
  TaskTemplateChartSplitNodeWidget,
  TaskTemplateChartMergeNodeWidget,
  TaskTemplateChartEndNodeWidget,
  EventNodeWidget,
} from "./TaskTemplateListItem";

interface WorkflowAsChartProps {
  handleClickTaskTemplate: (selectedTaskTemplate: TaskTemplate) => void;
}

type IConnectFlowObjects = (sourceTaskTemplateId: TaskTemplate["id"], targetTaskTemplateId: TaskTemplate["id"]) => void;

export const WorkflowAsChart = ({ handleClickTaskTemplate }: WorkflowAsChartProps) => {
  const {
    currentNextActivation,
    renderNextActivationForm,
    openNextActivationModal,
    connectFlowObjects,
    handleCloseNextActivationModal,
    onSaveActivationBranch,
    openBoundaryEventModal,
    handleCloseBoundaryEventModal,
    onClickBoundaryEvent,
    selectedBoundaryEvent,
    onSaveBoundaryEvent,
  } = useWorkflowAsChart();

  return (
    <ReactFlowProvider>
      <ReactFlowChart
        handleClickNode={(node) => {
          if (!!node.data.boundaryEvent) return onClickBoundaryEvent(node.data.boundaryEvent);
          handleClickTaskTemplate(node.data.taskTemplate);
        }}
        connectFlowObjects={connectFlowObjects}
      />
      {renderNextActivationForm && (
        <ActivationBranchForm
          nextActivationFetchId={null}
          nextActivationDefaultValues={currentNextActivation}
          updateNextActivationFromAPICallback={onSaveActivationBranch}
          onSuccessfulAPISave={null}
          open={openNextActivationModal}
          handleClose={handleCloseNextActivationModal}
        />
      )}
      {selectedBoundaryEvent && (
        <BoundaryEventConfigurationModal
          hostNodeId={selectedBoundaryEvent.host_node}
          boundaryEventConfiguration={selectedBoundaryEvent}
          onUpdateBoundaryEventConfiguration={onSaveBoundaryEvent}
          handleClose={handleCloseBoundaryEventModal}
          open={openBoundaryEventModal}
        />
      )}
    </ReactFlowProvider>
  );
};

const useWorkflowAsChart = () => {
  const {
    saveWorkflowStep,
    taskTemplateListContext: { findItem },
  } = useWorkflowContext();

  const { onSaveActivationBranchCallback } = useSplitNodeHandlers({ useWorkflowContext });
  const { onSaveBoundaryEvent } = useBoundaryEventHandlers();

  const [outgoingBranchState, setOutgoingBranchState] = React.useState({
    sourceNodeId: null,
    targetNodeId: null,
    activationConfiguration: null,
  });

  const [selectedBoundaryEvent, setSelectedBoundaryEvent] = React.useState<BoundaryEventConfiguration>(null);
  const {
    handleClose: handleCloseBoundaryEventModal,
    handleOpen: handleOpenBoundaryEventModal,
    open: openBoundaryEventModal,
  } = useToggleState();

  const {
    handleClose: handleCloseNextActivationModal,
    handleOpen: handleOpenNextActivationModal,
    open: openNextActivationModal,
  } = useToggleState();

  const onGatewayConnect = React.useCallback(
    (sourceTaskTemplate: TaskTemplate, targetTaskTemplate: TaskTemplate) => {
      setOutgoingBranchState({
        sourceNodeId: sourceTaskTemplate.id,
        targetNodeId: targetTaskTemplate.id,
        activationConfiguration: getNewActivationDefault({
          organizationId: sourceTaskTemplate.artifact_org,
          taskTemplateId: sourceTaskTemplate.id,
          otherProperties: {
            ontrue: targetTaskTemplate.id,
            description: `Outgoing Branch to ${targetTaskTemplate.title}`,
          },
        }),
      });

      handleOpenNextActivationModal();
    },
    [handleOpenNextActivationModal]
  );

  const connectFlowObjects = React.useCallback<IConnectFlowObjects>(
    (sourceNodeId, targetNodeId) => {
      if (sourceNodeId === targetNodeId) return; // Prevent connecting to self. No use case for such an endless loop.

      const sourceTaskTemplate = findItem(sourceNodeId)?.item;
      const targetTaskTemplate = findItem(targetNodeId)?.item;

      switch (sourceTaskTemplate?.type) {
        case "Split":
          onGatewayConnect(sourceTaskTemplate, targetTaskTemplate);
          break;
        default:
          saveWorkflowStep(sourceNodeId, targetNodeId);
      }
    },
    [saveWorkflowStep, onGatewayConnect, findItem]
  );

  const onSaveActivationBranch = (activationBranch: NextActivation) => {
    setOutgoingBranchState((prev) => ({ ...prev, activationConfiguration: activationBranch }));
    onSaveActivationBranchCallback(activationBranch);
  };

  return {
    currentNextActivation: outgoingBranchState.activationConfiguration,
    openNextActivationModal,
    renderNextActivationForm: outgoingBranchState?.sourceNodeId && outgoingBranchState?.targetNodeId,
    selectedBoundaryEvent,
    handleCloseNextActivationModal,
    onSaveActivationBranch,
    connectFlowObjects,
    openBoundaryEventModal,
    handleCloseBoundaryEventModal: () => {
      setSelectedBoundaryEvent(null);
      handleCloseBoundaryEventModal();
    },
    onClickBoundaryEvent: (boundaryEvent: BoundaryEventConfiguration) => {
      setSelectedBoundaryEvent(boundaryEvent);
      handleOpenBoundaryEventModal();
    },
    onSaveBoundaryEvent,
  };
};

interface ReactFlowChartProps extends Omit<WorkflowAsChartProps, "handleClickTaskTemplate"> {
  connectFlowObjects: IConnectFlowObjects;
  handleClickNode: (node: NodeProps<IReactFlowNodeData>) => void;
}

const ReactFlowChart = (props: ReactFlowChartProps) => {
  const {
    defaultZoom,
    defaultPosition,
    nodes,
    edges,
    nodeTypes,
    edgeTypes,
    fitToView,
    onNodeClick,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onMoveEnd,
    onNodeDragStop,
    onInitialize,
  } = useReactFlowChart(props);

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        className="os-workflow-flowchart-view-container"
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        fitView={fitToView}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onMoveEnd={onMoveEnd}
        onNodeDragStop={onNodeDragStop}
        onInit={onInitialize}
        defaultZoom={defaultZoom}
        defaultPosition={defaultPosition}
      >
        {/* <Background variant={BackgroundVariant.Dots} /> */}
        <MiniMap />
        <Controls showInteractive={false} />
      </ReactFlow>
    </>
  );
};

const useReactFlowChart = ({ connectFlowObjects, handleClickNode }: ReactFlowChartProps) => {
  const nodeTypes = React.useMemo(
    () => ({ mainSequenceFlowNode: TaskReactFlowNodeType, boundaryEventFlowNode: BoundaryEventReactFlowNodeType }),
    []
  );

  const edgeTypes = React.useMemo(
    () => ({
      taskTemplateNodeEdge: EdgeButton,
      boundaryEventNodeEdge: EdgeButton,
    }),
    []
  );

  const {
    nodes,
    edges,
    defaultViewportState,
    onNodesChange,
    onEdgesChange,
    persistViewPortState,
    persistNodePosition,
  } = useReactFlowContext();

  const onInitialize: OnInit = (reactFlowInstance) => {};

  const defaultZoom = defaultViewportState?.zoom ?? 1;

  const defaultPosition: [number, number] =
    defaultViewportState?.x && defaultViewportState?.y ? [defaultViewportState.x, defaultViewportState.y] : [0, 0];

  // const { getViewport } = useReactFlow();

  const onMoveEnd = React.useCallback<OnMoveEnd>(
    (event, viewport) => {
      // Avoid API call if the viewport (x,y) state has not changed. For now we avoid persisting
      // zoom i.e. event === null until we can improve the performance of a zoom as it persists.
      if (event === null) return;

      persistViewPortState(viewport);
    },
    [persistViewPortState]
  );

  const onNodeDragStop = React.useCallback<NodeDragHandler>(
    (event, node, nodes) => {
      persistNodePosition({ taskTemplate: node.data.taskTemplate, ...node.position });
    },
    [persistNodePosition]
  );

  const onConnect = React.useCallback<OnConnect>(
    (connection) => {
      connectFlowObjects(parseInt(connection.source), parseInt(connection.target));
    },
    [connectFlowObjects]
  );

  const onNodeClick = (event, node) => handleClickNode(node);

  // Fit to view if default position is react flow default and we have many nodes.
  // ReactFlow default values - https://reactflow.dev/docs/api/react-flow-props/
  const fitToView = nodes?.length > 10 && isEqual(defaultPosition, [0, 0]);

  return {
    defaultZoom,
    defaultPosition,
    nodes,
    edges,
    nodeTypes,
    edgeTypes,
    fitToView,
    onNodeClick,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onMoveEnd,
    onNodeDragStop,
    onInitialize,
  };
};

// const handleStyle = { left: 100 };

const foreignObjectWidth = 80;
const foreignObjectHeight = 40;

const EdgeButton = (props: EdgeProps<TaskTemplate>) => {
  const {
    edgePath,
    edgeCenterX,
    edgeCenterY,
    isShowEdgeActions,
    isDeletingEdge,
    sourceTaskTemplate,
    targetTaskTemplate,
    sourceIsSplit,
    nextActivationFetchId,
    openNextActivationModal,
    sourceIsBoundaryEvent,
    sourceBoundaryEvent,
    onHoverEdge,
    onConfirmDeleteEdge,
    onClickSettings,
    onSaveActivationBranchCallback,
    handleCloseNextActivationModal,
    onConfirmDeleteBoundaryEventEdge,
  } = useEdgeButton(props);

  return (
    <>
      <path
        id={props.id}
        style={props.style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={props.markerEnd}
        onMouseOver={onHoverEdge}
      />
      {isShowEdgeActions?.[props.id] && (
        <foreignObject
          width={foreignObjectWidth}
          height={foreignObjectHeight}
          x={edgeCenterX - foreignObjectWidth / 2}
          y={edgeCenterY - foreignObjectHeight / 2}
          className="edgebutton-foreignobject"
          requiredExtensions="http://www.w3.org/1999/xhtml"
        >
          <body>
            <Stack direction="row" spacing="5px">
              {sourceIsBoundaryEvent && sourceBoundaryEvent ? (
                <ActionDialog
                  severity="warning"
                  title="Remove Boundary Event"
                  confirmationAction={onConfirmDeleteBoundaryEventEdge}
                  isPeformingAction={isDeletingEdge}
                  dialogMessage={
                    <>
                      <p>
                        Are you sure you want to remove the boundary event '
                        <EmphasisText>{sourceBoundaryEvent.title}</EmphasisText>'? This will remove the activation of '
                        <EmphasisText>{sourceBoundaryEvent.target_node_title}</EmphasisText>' from '
                        <EmphasisText>{sourceBoundaryEvent.host_node_title}</EmphasisText>' when the '
                        <EmphasisText>{sourceBoundaryEvent.event_code_label}</EmphasisText>' event gets fired.
                      </p>
                    </>
                  }
                >
                  {({ handleOpen }) => (
                    <Tooltip title={<Typography>Remove Boundary Event</Typography>} placement="top" arrow>
                      <div
                        onClick={handleOpen}
                        style={{ background: COLORSCHEME.error, borderColor: COLORSCHEME.error }}
                        className="edgebutton"
                      >
                        <Trash2 size={16} />
                      </div>
                    </Tooltip>
                  )}
                </ActionDialog>
              ) : (
                <ActionDialog
                  severity="warning"
                  title="Delete Link Between Steps"
                  confirmationAction={onConfirmDeleteEdge}
                  isPeformingAction={isDeletingEdge}
                  dialogMessage={
                    <>
                      <p>Are you sure you want to delete this link?</p>
                      <p>
                        There will be no link between <EmphasisText>{sourceTaskTemplate?.title}</EmphasisText> and{" "}
                        <EmphasisText>{targetTaskTemplate?.title}</EmphasisText> in your workflow.
                      </p>
                    </>
                  }
                >
                  {({ handleOpen }) => (
                    <Tooltip title={<Typography>Delete Link Between Steps</Typography>} placement="top" arrow>
                      <div
                        onClick={handleOpen}
                        style={{ background: COLORSCHEME.error, borderColor: COLORSCHEME.error }}
                        className="edgebutton"
                      >
                        <Trash2 size={16} />
                      </div>
                    </Tooltip>
                  )}
                </ActionDialog>
              )}
              {sourceIsSplit && (
                <>
                  <Tooltip title={<Typography>Settings</Typography>} placement="top" arrow>
                    <div
                      onClick={onClickSettings}
                      style={{ background: COLORSCHEME.secondary, borderColor: COLORSCHEME.secondary }}
                      className="edgebutton"
                    >
                      <SettingsIcon size={16} />
                    </div>
                  </Tooltip>
                  <ActivationBranchForm
                    nextActivationFetchId={nextActivationFetchId}
                    nextActivationDefaultValues={null}
                    updateNextActivationFromAPICallback={onSaveActivationBranchCallback}
                    onSuccessfulAPISave={null}
                    open={openNextActivationModal}
                    handleClose={handleCloseNextActivationModal}
                  />
                </>
              )}
            </Stack>
          </body>
        </foreignObject>
      )}
    </>
  );
};

const useEdgeButton = ({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps<TaskTemplate>) => {
  const edgePath = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const [edgeCenterX, edgeCenterY] = getEdgeCenter({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const [isDeletingEdge, setIsDeletingEdge] = React.useState(false);

  const {
    deleteWorkflowEdge,
    taskTemplateListContext: { findItem, selectItem },
  } = useWorkflowContext();
  const { deleteBoundaryEvent, getBoundaryEventFromHostNodeId } = useBoundaryEventHandlers();

  let sourceTaskTemplate: TaskTemplate = null;
  let targetTaskTemplate: TaskTemplate = null;
  let sourceIsSplit = false;
  let sourceBoundaryEvent: BoundaryEventConfiguration = null;
  let boundaryEventId = null;
  let boundaryEventHostNodeId = null;
  const sourceIsBoundaryEvent = source.startsWith("boundary");

  if (sourceIsBoundaryEvent) {
    boundaryEventId = parseInt(source.split("_")[1]);
    boundaryEventHostNodeId = parseInt(source.split("_")[3]);
    sourceBoundaryEvent = getBoundaryEventFromHostNodeId(boundaryEventId, boundaryEventHostNodeId);
  } else {
    sourceTaskTemplate = findItem(parseInt(source)).item;
    targetTaskTemplate = findItem(parseInt(target)).item;
    sourceIsSplit = sourceTaskTemplate?.type === "Split";
  }

  const isShowEdgeActions = useWorkflowStore((state) => state.isShowEdgeActions);
  const showEdgeActions = useWorkflowStore((state) => state.showEdgeActions);
  const openTaskTemplateFormFn = useWorkflowStore((state) => state.openTaskTemplateFormFn);

  const onHoverEdge = () => showEdgeActions(id);

  const onConfirmDeleteBoundaryEventEdge = () => {
    if (!sourceIsBoundaryEvent) return;
    setIsDeletingEdge(true);
    return deleteBoundaryEvent(boundaryEventId, boundaryEventHostNodeId);
  };

  const onConfirmDeleteEdge = () => {
    setIsDeletingEdge(true);
    deleteWorkflowEdge(sourceTaskTemplate?.id, targetTaskTemplate?.id);
  };

  const { onSaveActivationBranchCallback } = useSplitNodeHandlers({ useWorkflowContext });

  const {
    handleClose: handleCloseNextActivationModal,
    handleOpen: handleOpenNextActivationModal,
    open: openNextActivationModal,
  } = useToggleState();

  const nextActivations = sourceTaskTemplate?.outgoing_branches?.filter(
    (outgoing_summary) => outgoing_summary.next_id === targetTaskTemplate?.id
  );

  const nextActivationFetchId = nextActivations?.length === 1 ? nextActivations?.[0]?.activation_settings_id : null;

  const handleOpenTaskTemplateForm = React.useCallback(() => {
    if (!sourceTaskTemplate?.id) return;
    selectItem(sourceTaskTemplate.id);
    openTaskTemplateFormFn();
  }, [sourceTaskTemplate?.id, selectItem, openTaskTemplateFormFn]);

  const onClickSettings = () => {
    if (nextActivationFetchId) {
      handleOpenNextActivationModal();
      return;
    }

    handleOpenTaskTemplateForm();
  };

  return {
    edgePath,
    edgeCenterX,
    edgeCenterY,
    isShowEdgeActions,
    isDeletingEdge,
    sourceTaskTemplate,
    targetTaskTemplate,
    sourceIsSplit,
    nextActivationFetchId,
    openNextActivationModal,
    sourceIsBoundaryEvent,
    sourceBoundaryEvent,
    onHoverEdge,
    onConfirmDeleteEdge,
    onClickSettings,
    onSaveActivationBranchCallback,
    handleCloseNextActivationModal,
    onConfirmDeleteBoundaryEventEdge,
  };
};

const BoundaryEventReactFlowNodeType = React.memo(({ data: { boundaryEvent } }: NodeProps<IReactFlowNodeData>) => {
  return (
    <>
      <EventNodeWidget
        id={`${boundaryEvent.id}`}
        title={boundaryEvent.title}
        isInterrupting={boundaryEvent.is_interrupting}
        eventDirection="catch"
        eventType={boundaryEvent.event_type}
        size={BOUNDARY_EVENT_NODE_SIZE}
        showLabel={false}
      />
      <Handle type="source" position={Position.Bottom} id={getBoundaryEventNodeId(boundaryEvent)} />
    </>
  );
});

const TaskReactFlowNodeType = React.memo(({ data: { taskTemplate } }: NodeProps<IReactFlowNodeData>) => {
  const {
    taskTemplateListContext: {
      itemsListState: { selectedItemId },
    },
  } = useWorkflowContext();

  const isStartNode = taskTemplate.type === "StartFunction";

  let nodeComponent: React.ReactNode;

  switch (taskTemplate.type) {
    case "Join":
      nodeComponent = (
        <TaskTemplateChartMergeNodeWidget
          taskTemplate={taskTemplate}
          selected={selectedItemId === taskTemplate.id}
          onClick={() => {}}
          handleListItemEdit={() => {}}
        />
      );
      break;
    case "Split":
      nodeComponent = (
        <TaskTemplateChartSplitNodeWidget
          taskTemplate={taskTemplate}
          selected={selectedItemId === taskTemplate.id}
          onClick={() => {}}
          handleListItemEdit={() => {}}
        />
      );
      break;
    case "End":
      nodeComponent = (
        <TaskTemplateChartEndNodeWidget
          taskTemplate={taskTemplate}
          selected={selectedItemId === taskTemplate.id}
          onClick={() => {}}
          handleListItemEdit={() => {}}
        />
      );
      break;
    case "Event":
      nodeComponent = (
        <EventNodeWidget
          id={`${taskTemplate.id}`}
          title={taskTemplate.title}
          isInterrupting={false}
          eventDirection={taskTemplate.event_direction}
          eventType={taskTemplate.event_type}
          size={50}
          showTooltip={false}
        />
      );
      break;
    default:
      nodeComponent = (
        <div style={{ width: 500 }}>
          <TaskTemplateListItemDefaultWidget
            taskTemplate={taskTemplate}
            selected={selectedItemId === taskTemplate.id}
            onClick={() => {}}
            handleListItemEdit={() => {}}
          />
        </div>
      );
  }

  return (
    <>
      {!isStartNode && <Handle type="target" position={Position.Top} />}

      {nodeComponent}

      <Handle type="source" position={Position.Bottom} id={`${taskTemplate.id}`} />
      {/* <Handle type="source" position={Position.Bottom} id="b" style={handleStyle} /> */}
    </>
  );
});
